import { ReactNode } from 'react';

import { CustomSearchInputClassList } from 'component/Search/Input/types';

import { QaIdentifier } from 'hooks/useQa';

import { ReducerAction } from 'types/reducer';
import { Validator } from 'types/validator';

export type SearchResultProvider<ResultEntry> = (
    searchValue: string
) => Promise<Array<ResultEntry>>;

export interface AutocompleteConfig<ResultEntry> {
    searchResultProvider: SearchResultProvider<ResultEntry>;
    threshold: number;
}

export interface AutocompleteState<ResultEntry, Value> {
    config: AutocompleteConfig<ResultEntry>;
    currentValue: Set<Value>;
    recentValue: Set<Value>;
    lastModified?: number;
    results?: Array<ResultEntry>;
}

export enum AutocompleteTaskDefs {
    SetResults = 'SetResults',
    SetValue = 'SetValue',
    Reset = 'Reset',
    Clear = 'Clear',
    SetThreshold = 'SetThreshold',
}

type Payload<PayloadValue> = {
    value: PayloadValue;
    timestamp?: number;
};

export type AutocompleteReducerAction<ResultEntry, Value> =
    | ReducerAction<
          AutocompleteTaskDefs.SetResults,
          Payload<Array<ResultEntry>>
      >
    | ReducerAction<AutocompleteTaskDefs.SetValue, Payload<Set<Value>>>
    | ReducerAction<AutocompleteTaskDefs.Reset, Payload<Set<Value>>>
    | ReducerAction<AutocompleteTaskDefs.Clear, { timestamp?: number }>
    | ReducerAction<AutocompleteTaskDefs.SetThreshold, Payload<number>>;

export interface AutocompleteActionMap<ResultEntry, Value> {
    setResults: (results: Array<ResultEntry>) => void;
    setValue: (value: Set<Value>) => void;
    reset: (value: Set<Value>) => void;
    clear: () => void;
    config: {
        setThreshold: (threshold: number) => void;
    };
}

export interface ListProps<T> {
    entries: T[];
    onClick?: (entry: T, resultList?: string) => void;
}

export interface ACCustomClass extends CustomSearchInputClassList {
    autocompleterWrapper?: string;
}

export interface AutocompleterProps {
    children: ReactNode;
    errorMessage?: ReactNode;
    toggle?: boolean;
    searchValue?: string;
    placeholder: string;
    label: string;
    customClassList?: ACCustomClass;
    autocompleterWrapperRef?: React.RefObject<HTMLDivElement>;
    validatorList?: Array<Validator<string>>; // it's string, because we want to check the input
    withClearButton?: boolean;
    onSearch?: (value: string) => void;
    onSearchInputClick?: () => void;
    onBlur?: () => void;
    onFocus?: () => void;
    onClear?: () => void;
    qaId?: QaIdentifier;
}
